import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, MenuItem, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { addSite, checkSite, getAlertSettings, listChanges, listSites, markSeen, removeSite, saveAlertSettings, sendTestAlert, syncSites, updateSite } from 'api/siteWatch';
import { GREEN, MONO, ago } from './scanUi';
import { FILTERS, KIND, LEVELS, SEV, certColor, certText, counters, hostOf, levelText, ordered, siteState } from './siteWatchLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.url?.[0] || e?.response?.data?.detail || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

function Change({ c, showSite }) {
  const [open, setOpen] = useState(false);
  const d = c.detail || {};
  const lines = d.added || d.removed;
  const canOpen = Boolean(d.added?.length || d.removed?.length);
  return (
    <Box sx={{ px: 2.8, py: 1.6, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SEV[c.severity] || '#64748b', flex: 'none' }} />
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{c.summary}</Typography>
          {showSite && <Typography variant="caption" sx={{ fontFamily: MONO }} color="text.secondary">{hostOf(c.url)}</Typography>}
        </Box>
        {!c.seen && <Pill color={GREEN}>NEW</Pill>}
        <Pill color="#94a3b8">{(KIND[c.kind] || c.kind).toUpperCase()}</Pill>
        <Pill color={SEV[c.severity] || '#64748b'}>{c.severity.toUpperCase()}</Pill>
        <Typography variant="caption" color="text.secondary" sx={{ width: 84, textAlign: 'right' }}>{ago(c.created_at)}</Typography>
        {canOpen && <Button size="small" color="inherit" onClick={() => setOpen(!open)} sx={{ textTransform: 'none' }}>{open ? 'Hide' : 'See what changed'}</Button>}
      </Stack>
      {open && lines && (
        <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1.2 }}>
          {[['ADDED', d.added, GREEN, '+'], ['REMOVED', d.removed, '#ef4444', '−']].filter(([, l]) => l?.length).map(([label, list, color, sign]) => (
            <Box key={label} sx={{ flex: '1 1 260px', p: 1.4, borderRadius: 2, border: 1, borderColor: alpha(color, 0.35), bgcolor: alpha(color, 0.07) }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color, mb: 0.5 }}>{label}</Typography>
              {list.map((x, i) => <Typography key={i} variant="body2" color="text.secondary" sx={{ py: 0.2 }}>{sign} {x}</Typography>)}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function WebsiteChangesPage() {
  const { currentTargetId } = useFuzzScanTarget();
  const [sites, setSites] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sites');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('all');
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [prefs, setPrefs] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([listSites(), listChanges('?limit=300')]);
      setSites(Array.isArray(s) ? s : s.results || []);
      setAll(c.results || []);
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your websites.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); getAlertSettings().then(setPrefs).catch(() => {}); }, [load]);

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try { setPrefs(await saveAlertSettings(patch)); } catch (e) { setPrefs(prefs); setToast({ severity: 'error', message: fail(e, 'Could not save that.') }); }
  };
  const testEmail = async () => {
    try { const r = await sendTestAlert(); setToast({ severity: 'success', message: `A test email was sent to ${r.sent_to}.` }); } catch (e) { setToast({ severity: 'error', message: fail(e, 'The test email could not be sent.') }); }
  };

  const waiting = sites.some((s) => s.enabled && !s.last_checked_at);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [waiting, load]);

  const sorted = useMemo(() => ordered(sites), [sites]);
  const sum = useMemo(() => counters(sites, all), [sites, all]);
  const selected = sites.find((s) => s.id === selectedId) || sorted[0] || null;

  useEffect(() => {
    if (!selected) { setHistory([]); return undefined; }
    let live = true;
    listChanges(`?watch=${selected.id}&limit=100`).then((d) => {
      if (!live) return;
      setHistory(d.results || []);
      if ((d.results || []).some((c) => !c.seen)) markSeen(selected.id).then(() => live && load()).catch(() => {});
    }).catch(() => live && setHistory([]));
    return () => { live = false; };
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) setToast({ severity: 'success', message: typeof ok === 'function' ? ok(r) : ok }); await load(); } catch (e) { setToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    setBusy(false);
  };
  const feed = useMemo(() => all.filter(FILTERS.find((f) => f.id === filter).test), [all, filter]);
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 170px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Website changes</Typography>
        <Typography color="text.secondary" variant="body2">Each website with what it runs and what changed on it</Typography>
      </Box>

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Websites watched', sum.watched, 'target and subdomains')}
        {stat('New changes', sum.unseen, 'not seen yet', sum.unseen ? '#fcd34d' : undefined)}
        {stat('Changed this week', sum.week, `across ${sum.weekSites} site${sum.weekSites === 1 ? '' : 's'}`)}
        {stat('Need a look', sum.attention, 'medium or high', sum.attention ? '#f87171' : GREEN)}
      </Stack>

      {prefs && (
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ ...panel, px: 2.4, py: 1.6 }}>
          <Switch color="success" checked={prefs.email_enabled} onChange={(e) => savePrefs({ email_enabled: e.target.checked })} />
          <Box sx={{ flex: '1 1 260px' }}>
            <Typography sx={{ fontWeight: 600 }}>Email me when a website changes</Typography>
            <Typography variant="caption" color="text.secondary">{levelText(prefs)} One email per check, sent to the address of your account.</Typography>
          </Box>
          <TextField select size="small" label="Tell me about" value={prefs.min_severity} disabled={!prefs.email_enabled} onChange={(e) => savePrefs({ min_severity: e.target.value })} sx={{ minWidth: 220 }}>
            {LEVELS.map((l) => <MenuItem key={l.id} value={l.id}><Box><Typography variant="body2">{l.label}</Typography><Typography variant="caption" color="text.secondary">{l.hint}</Typography></Box></MenuItem>)}
          </TextField>
          <Button size="small" variant="outlined" color="inherit" onClick={testEmail}>Send me a test email</Button>
        </Stack>
      )}

      <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup exclusive size="small" value={tab} onChange={(_, v) => v && setTab(v)}>
          <ToggleButton value="sites" sx={{ px: 2, fontWeight: 600, textTransform: 'none' }}>Sites</ToggleButton>
          <ToggleButton value="feed" sx={{ px: 2, fontWeight: 600, textTransform: 'none' }}>All changes</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Add a website, for example https://blog.example.com/" value={adding} onChange={(e) => setAdding(e.target.value)} sx={{ flex: '1 1 300px', maxWidth: 420, '& input': { fontFamily: MONO, fontSize: 13 } }} />
        <Button variant="outlined" color="inherit" disabled={busy || !adding.trim()} onClick={() => run(() => addSite(adding.trim()).then(() => setAdding('')), 'Added. We are looking at it now.')}>Add</Button>
        <Button variant="contained" disabled={busy || !currentTargetId} onClick={() => run(() => syncSites(currentTargetId), (r) => (r.added ? `${r.added} website${r.added === 1 ? '' : 's'} added.` : 'All your websites are already watched.'))} sx={{ ml: 'auto', fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Watch my sites</Button>
      </Stack>

      {!sites.length && (
        <Box sx={{ ...panel, p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>No websites watched yet</Typography>
          <Typography color="text.secondary">Press "Watch my sites" to watch your target and every subdomain found so far. After the first look, we tell you what changes.</Typography>
        </Box>
      )}

      {sites.length > 0 && tab === 'sites' && (
        <Stack direction="row" gap={2.5} flexWrap="wrap" alignItems="flex-start">
          <Box sx={{ ...panel, flex: '1 1 300px', overflow: 'hidden', maxHeight: 720, overflowY: 'auto' }}>
            {sorted.map((s) => {
              const st = siteState(s);
              return (
                <Stack key={s.id} direction="row" alignItems="center" gap={1.5} onClick={() => setSelectedId(s.id)} sx={{ p: 1.6, borderTop: 1, borderColor: 'divider', cursor: 'pointer', bgcolor: selected?.id === s.id ? alpha('#fff', 0.05) : 'transparent', '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flex: 'none' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: 13.5, fontWeight: 600 }} noWrap>{hostOf(s.url)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{st.label}</Typography>
                  </Box>
                  {s.unseen > 0 && <Pill color={SEV[s.latest_change?.severity] || '#f59e0b'}>{s.unseen} NEW</Pill>}
                </Stack>
              );
            })}
          </Box>

          {selected && (
            <Box sx={{ ...panel, flex: '2 1 520px', overflow: 'hidden', borderColor: 'text.disabled' }}>
              <Box sx={{ p: 2.4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
                  <Box>
                    <Typography sx={{ fontSize: 19, fontWeight: 700, fontFamily: MONO }}>{hostOf(selected.url)}</Typography>
                    <Typography variant="body2" color="text.secondary">{selected.title || 'No title'}{selected.last_status ? ` · status ${selected.last_status}` : ''}{selected.last_checked_at ? ` · checked ${ago(selected.last_checked_at)}` : ''}</Typography>
                  </Box>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Watching</Typography>
                    <Switch color="success" checked={selected.enabled} onChange={(e) => run(() => updateSite(selected.id, { enabled: e.target.checked }), e.target.checked ? 'Watching again.' : 'Paused.')} />
                    <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={() => run(() => checkSite(selected.id), 'Checking now. Refresh in a moment.')}>Check now</Button>
                    <Button size="small" color="error" onClick={() => window.confirm(`Stop watching ${hostOf(selected.url)}? Its history goes too.`) && run(() => removeSite(selected.id).then(() => setSelectedId(null)), 'Removed.')}>Remove</Button>
                  </Stack>
                </Stack>
                <Stack direction="row" gap={0.8} flexWrap="wrap" sx={{ mt: 1.4 }}>
                  {(selected.technologies || []).map((t) => <Pill key={t} color="#94a3b8">{t}</Pill>)}
                  {selected.certificate_days != null && <Pill color={certColor(selected.certificate_days)}>{certText(selected.certificate_days).toUpperCase()}</Pill>}
                </Stack>
              </Box>
              <Typography sx={{ px: 2.4, py: 1.4, fontWeight: 700, borderTop: 1, borderColor: 'divider' }}>History</Typography>
              {!history.length && <Typography color="text.secondary" sx={{ p: 2.4, borderTop: 1, borderColor: 'divider' }}>{selected.last_checked_at ? 'Nothing has changed since the first look.' : 'We are taking the first look. Changes will show here.'}</Typography>}
              {history.map((c) => <Change key={c.id} c={{ ...c, url: selected.url }} />)}
            </Box>
          )}
        </Stack>
      )}

      {sites.length > 0 && tab === 'feed' && (
        <>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {FILTERS.map((f) => {
              const on = filter === f.id;
              return <Button key={f.id} onClick={() => setFilter(f.id)} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: on ? 'text.secondary' : 'divider', color: on ? 'text.primary' : 'text.secondary' }}>{f.label} {all.filter(f.test).length}</Button>;
            })}
            <Button size="small" variant="outlined" color="inherit" sx={{ ml: 'auto' }} disabled={busy || !sum.unseen} onClick={() => run(() => markSeen(), 'All marked as seen.')}>Mark all as seen</Button>
          </Stack>
          <Box sx={{ ...panel, overflow: 'hidden' }}>
            {!feed.length && <Typography color="text.secondary" sx={{ p: 3 }}>{all.length ? 'Nothing matches that.' : 'No changes yet. They show up here after the first daily check.'}</Typography>}
            {feed.map((c) => <Change key={c.id} c={c} showSite />)}
          </Box>
        </>
      )}
      <Typography variant="body2" color="text.secondary">Each website is looked at once a day, from the outside. The first look is the starting point. After that we tell you what is different: the page, the title, versions, redirects, security headers and the certificate. Numbers that always change, like dates and counters, are ignored. Alerts are sent by email, and you choose above how much you want to hear about.</Typography>
      <Snackbar open={Boolean(toast)} autoHideDuration={4500} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
